import { Injectable } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AutomaticMissionConfigurationDto,
    DO_NOTHING,
    EnhancedHelpDto,
    FEATURE_FLIGHT_LOG_ALERTS,
    FlightLogCollectionService,
    flightLogCollectionSourceDisplayableNames,
    FlightLogCollectionSourceDto,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    MissionObjectivesService,
    MissionService,
    MissionWorkflowService,
    OrganisationAutomaticMissionConfigurationDto,
    PersonService,
    ProjectsService,
    UpdateAutomaticMissionConfigurationCommand,
    UpdateOrganisationAutomaticMissionConfigurationCommand,
    UserService
} from '@flyfreely-portal-ui/flyfreely';
import { FormatPersonPipe } from '@flyfreely-portal-ui/ui';
import { pipe } from 'fp-ts/es6/function';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { EnhancedHelpService } from 'libs/enhanced-help/src/lib/enhanced-help.service';
import { FormlyPilotHelperService } from 'libs/flight-logs/src/lib/settings/formly-pilot-helper.service';
import { WorkGroupsService } from 'libs/flyfreely/src/lib/services/workgroups.service';
import {
    combineLatest,
    firstValueFrom,
    Observable,
    ReplaySubject,
    Subject,
    take
} from 'rxjs';
import { shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';

export const automaticMissionSettingsTypeDisplayableNames = {
    [AutomaticMissionConfigurationDto.Type.ORGANISATION_DEFAULT]:
        'Organisation Default',
    [AutomaticMissionConfigurationDto.Type.MISSION_SUGGESTION]:
        'Suggest Mission Assignment',
    [AutomaticMissionConfigurationDto.Type.MISSION_ASSIGNMENT]:
        'Automatic Assignment',
    [AutomaticMissionConfigurationDto.Type.MISSION_CREATION]:
        'Automatic Mission Creation',
    [AutomaticMissionConfigurationDto.Type.DISABLED]: 'Disabled'
};

@Injectable()
export class FlightLogSettingsService {
    private ngUnsubscribe$ = new Subject<void>();

    flightLogCollectionSourceList$: Observable<FlightLogCollectionSourceDto[]>;

    private organisationIdSource = new ReplaySubject<number>(1);

    canUseAlerts = false;
    organisationId: number;

    constructor(
        private flightLogCollectionService: FlightLogCollectionService,
        private commonDialoguesService: CommonDialoguesService,
        private userService: UserService,
        private enhancedHelpService: EnhancedHelpService,
        private personService: PersonService,
        private missionService: MissionService,
        private missionWorkflowService: MissionWorkflowService,
        private workgroupsSource: WorkGroupsService,
        private projectService: ProjectsService,
        private missionObjectiveService: MissionObjectivesService,
        private logging: FlyFreelyLoggingService,
        private formatPersonPipe: FormatPersonPipe
    ) {
        this.flightLogCollectionSourceList$ = combineLatest([
            this.organisationIdSource,
            this.flightLogCollectionService.change$.pipe(startWith([undefined]))
        ]).pipe(
            takeUntil(this.ngUnsubscribe$),
            switchMap(([organisationId, _]) =>
                this.flightLogCollectionService.find(organisationId)
            ),
            shareReplay(1)
        );
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setOrganisation(organisationId: number) {
        this.organisationIdSource.next(organisationId);

        this.organisationId = organisationId;

        this.canUseAlerts = pipe(
            this.userService.findOrganisationForUser(this.organisationId),
            map(o => hasFeatureFlag(o, FEATURE_FLIGHT_LOG_ALERTS)),
            getOrElse(() => false)
        );
    }

    showAlertSettings(entry: FlightLogCollectionSourceDto) {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Update Alert Settings',
                'Save',
                true,
                true,
                [
                    {
                        key: `section-alerts`,
                        type: 'section-heading',
                        props: {
                            label: 'Alerts'
                        }
                    },
                    {
                        key: 'sevenDays',
                        type: 'checkbox',
                        props: {
                            label: 'Alert on 7 days without flight logs',
                            required: false
                        }
                    },
                    {
                        key: 'thirtyDays',
                        type: 'checkbox',
                        props: {
                            label: 'Alert on 30 days without flight logs',
                            required: false
                        }
                    },
                    {
                        key: 'sixtyDays',
                        type: 'checkbox',
                        props: {
                            label: 'Alert on 60 days without flight logs',
                            required: false
                        }
                    },
                    {
                        key: 'invalidPassword',
                        type: 'checkbox',
                        props: {
                            label: 'Alert on invalid password',
                            required: false
                        }
                    },
                    {
                        key: `section-notifications`,
                        type: 'section-heading',
                        props: {
                            label: 'Who to notify'
                        }
                    },
                    {
                        key: 'notifyOwner',
                        type: 'checkbox',
                        props: {
                            label: 'Notify organisation owner',
                            required: false
                        }
                    },
                    {
                        key: 'notifyOrganisationAdmins',
                        type: 'checkbox',
                        props: {
                            label: 'Notify organisation admins',
                            required: false
                        }
                    },
                    {
                        key: 'notifyOrganisationCrps',
                        type: 'checkbox',
                        props: {
                            label: 'Notify organisation CRPs',
                            required: false
                        }
                    }
                ],
                entry.alertSettings != null
                    ? {
                          sevenDays:
                              entry.alertSettings.daysSinceLastFile.indexOf(
                                  7
                              ) !== -1,
                          thirtyDays:
                              entry.alertSettings.daysSinceLastFile.indexOf(
                                  30
                              ) !== -1,
                          sixtyDays:
                              entry.alertSettings.daysSinceLastFile.indexOf(
                                  60
                              ) !== -1,
                          invalidPassword: entry.alertSettings.invalidPassword,
                          notifyOrganisationAdmins:
                              entry.alertSettings.notifyOrganisationAdmins,
                          notifyOrganisationCrps:
                              entry.alertSettings.notifyOrganisationCrps,
                          notifyOwner: entry.alertSettings.notifyOwner
                      }
                    : {},
                data => {
                    const daysSinceLastFile = [
                        data.sevenDays ? 7 : undefined,
                        data.thirtyDays ? 30 : undefined,
                        data.sixtyDays ? 60 : undefined
                    ].filter(e => e !== undefined);

                    return firstValueFrom(
                        this.flightLogCollectionService.updateAlertSettings(
                            entry.id,
                            {
                                daysSinceLastFile,
                                notificationRecipientList: [],
                                invalidPassword: data.invalidPassword ?? false,
                                notifyOrganisationAdmins:
                                    data.notifyOrganisationAdmins ?? false,
                                notifyOrganisationCrps:
                                    data.notifyOrganisationCrps ?? false,
                                notifyOwner: data.notifyOwner ?? false
                            }
                        )
                    );
                },
                'modal-task'
            )
            .then(DO_NOTHING)
            .catch(DO_NOTHING);
    }

    showAutomaticMissionConfigurationSettings(
        entry: FlightLogCollectionSourceDto,
        config: AutomaticMissionConfigurationDto
    ) {
        combineLatest([
            this.enhancedHelpService.findByScreen(
                'flight-log-mission-creation-settings'
            ),
            this.missionService.findMissionTypes(this.organisationId),
            this.missionWorkflowService.findWorkflows(this.organisationId),
            this.workgroupsSource.findWorkgroups(this.organisationId),
            this.flightLogCollectionService.findOrganisationAutomaticMissionConfiguration(
                this.organisationId
            ),
            this.projectService.find(this.organisationId, false),
            this.missionObjectiveService.find(this.organisationId, false)
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([
                    help,
                    missionTypeList,
                    workflowList,
                    workgroupList,
                    organisationDefaults,
                    projectList,
                    missionObjectiveList
                ]) => {
                    const pilotService = new FormlyPilotHelperService(
                        this.personService,
                        this.logging,
                        this.formatPersonPipe
                    );
                    pilotService.setOrganisationId(this.organisationId);
                    pilotService.ngOnInit();

                    const helpText =
                        help == null
                            ? {}
                            : help.reduce(
                                  (
                                      acc: { [component: string]: string },
                                      h: EnhancedHelpDto
                                  ) => ({ ...acc, [h.component]: h.helpText }),
                                  {}
                              );
                    const sourceName =
                        flightLogCollectionSourceDisplayableNames[entry.type];

                    const ownerName =
                        entry.username != null
                            ? `for the account with username ${entry.username}`
                            : entry.owner != null
                            ? `for the account owned by ${entry.owner.firstName} ${entry.owner.lastName}`
                            : 'for this account';

                    this.commonDialoguesService
                        .showFormlyDialogue<
                            Omit<
                                UpdateAutomaticMissionConfigurationCommand,
                                'collectionSourceId'
                            >,
                            AutomaticMissionConfigurationDto
                        >(
                            `Automatic Mission Creation Settings`,
                            'Save',
                            true,
                            true,
                            [
                                {
                                    key: `section-required-info`,
                                    type: 'info',
                                    props: {
                                        // Can't use enhanced help for text here due to contextual information required.
                                        message: `These settings will be applied for any flight logs collected via ${sourceName} cloud syncing ${ownerName}`
                                    }
                                },
                                {
                                    key: 'type',
                                    type: 'ng-select',
                                    props: {
                                        label: 'Type (action)',
                                        required: true,
                                        description: helpText['type'],
                                        labelProp: 'name',
                                        valueProp: 'value',
                                        options: [
                                            {
                                                name: 'Organisation Default',
                                                value: 'ORGANISATION_DEFAULT'
                                            },
                                            {
                                                name: 'Suggest Mission Assignment',
                                                value: 'MISSION_SUGGESTION'
                                            },
                                            {
                                                name: 'Automatic Assignment',
                                                value: 'MISSION_ASSIGNMENT'
                                            },
                                            {
                                                name: 'Automatic Mission Creation',
                                                value: 'MISSION_CREATION'
                                            },
                                            {
                                                name: 'Disabled',
                                                value: 'DISABLED'
                                            }
                                        ]
                                    }
                                },
                                {
                                    key: `section-required`,
                                    type: 'section-heading',
                                    hideExpression:
                                        'model.type === "DISABLED" || model.type === "ORGANISATION_DEFAULT"',
                                    props: {
                                        label: 'Required parameters'
                                    }
                                },
                                {
                                    key: `section-required-info`,
                                    type: 'info',
                                    hideExpression:
                                        'model.type === "DISABLED" || model.type === "ORGANISATION_DEFAULT"',
                                    props: {
                                        message:
                                            helpText['section-required-info']
                                    }
                                },
                                {
                                    key: 'maxDistance',
                                    type: 'input',
                                    hideExpression:
                                        'model.type === "DISABLED" || model.type === "ORGANISATION_DEFAULT"',
                                    props: {
                                        label: 'Maximum distance (m)',
                                        type: 'number',
                                        required: true,
                                        description: helpText['maxDistance']
                                    }
                                },
                                {
                                    key: 'maxTimeDifference',
                                    type: 'duration',
                                    hideExpression:
                                        'model.type === "DISABLED" || model.type === "ORGANISATION_DEFAULT"',
                                    props: {
                                        label: 'Maximum time difference',
                                        required: true,
                                        description:
                                            helpText['maxTimeDifference']
                                    }
                                },
                                {
                                    key: 'requireRpa',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type === "DISABLED" || model.type === "ORGANISATION_DEFAULT"',
                                    props: {
                                        label: 'Log requires an RPA',
                                        required: true,
                                        description: helpText['requireRpa']
                                    }
                                },
                                {
                                    key: `section-optional`,
                                    type: 'section-heading',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Optional parameters'
                                    }
                                },
                                {
                                    key: `section-optional-info`,
                                    type: 'info',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        message:
                                            helpText['section-optional-info']
                                    }
                                },
                                {
                                    key: 'useCollectionSourceOwner',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_ASSIGNMENT" && model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Collection Source Owner is Pilot',
                                        required: true,
                                        description:
                                            helpText['useCollectionSourceOwner']
                                    }
                                },
                                {
                                    key: 'remotePilotId',
                                    type: 'graphql-select',
                                    hideExpression:
                                        'model.type !== "MISSION_ASSIGNMENT" && model.type !== "MISSION_CREATION" || model.useCollectionSourceOwner === true',
                                    props: {
                                        label: 'Pilot',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        service: pilotService,
                                        clearable: true,
                                        required: false,
                                        validateSelection: true,
                                        description: helpText['remotePilotId']
                                    }
                                },
                                {
                                    key: 'operationTypeId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Mission Type',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: missionTypeList,
                                        clearable: true,
                                        required: false,
                                        description: helpText['operationTypeId']
                                    }
                                },
                                {
                                    key: 'missionWorkflowId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Workflow',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: workflowList,
                                        clearable: true,
                                        required: false,
                                        description:
                                            helpText['missionWorkflowId']
                                    }
                                },
                                {
                                    key: 'workgroupId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Workgroup',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: workgroupList,
                                        clearable: true,
                                        required: false,
                                        description: helpText['workgroupId']
                                    }
                                },
                                ...(projectList.length > 0
                                    ? [
                                          {
                                              key: 'projectId',
                                              type: 'ng-select',
                                              hideExpression:
                                                  'model.type !== "MISSION_CREATION"',
                                              props: {
                                                  label: 'Project',
                                                  labelProp: 'name',
                                                  valueProp: 'id',
                                                  options: projectList,
                                                  clearable: true,
                                                  required: false,
                                                  description:
                                                      helpText['projectId']
                                              }
                                          }
                                      ]
                                    : []),
                                ...(missionObjectiveList.length > 0
                                    ? [
                                          {
                                              key: 'missionObjectiveId',
                                              type: 'ng-select',
                                              hideExpression:
                                                  'model.type !== "MISSION_CREATION"',
                                              props: {
                                                  label: 'Mission Objective',
                                                  labelProp: 'name',
                                                  valueProp: 'id',
                                                  options: missionObjectiveList,
                                                  clearable: true,
                                                  required: false,
                                                  description:
                                                      helpText[
                                                          'missionObjectiveId'
                                                      ]
                                              }
                                          }
                                      ]
                                    : []),
                                {
                                    key: 'createFlightOnAssignment',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_ASSIGNMENT"',
                                    props: {
                                        label: 'Create Flight on Assigned Mission',
                                        required: true,
                                        description:
                                            helpText['createFlightOnAssignment']
                                    }
                                },
                                {
                                    key: 'missionPerRpa',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Separate Mission per RPA',
                                        required: true,
                                        description: helpText['missionPerRpa']
                                    }
                                }
                            ],
                            config != null
                                ? {
                                      type: config.type,
                                      maxDistance: config.maxDistance,
                                      maxTimeDifference:
                                          config.maxTimeDifference,
                                      useCollectionSourceOwner:
                                          config.useCollectionSourceOwner,
                                      remotePilotId: config.remotePilot?.id,
                                      operationTypeId: config.operationType?.id,
                                      missionWorkflowId:
                                          config.missionWorkflow?.id,
                                      workgroupId: config.workgroup?.id,
                                      projectId: config.project?.id,
                                      missionObjectiveId:
                                          config.missionObjective?.id,
                                      requireRpa: config.requireRpa,
                                      createFlightOnAssignment:
                                          config.createFlightOnAssignment,
                                      missionPerRpa: config.missionPerRpa
                                  }
                                : {
                                      type: 'ORGANISATION_DEFAULT',
                                      maxDistance:
                                          organisationDefaults.maxDistance,
                                      maxTimeDifference:
                                          organisationDefaults.maxTimeDifference,
                                      requireRpa:
                                          organisationDefaults.requireRpa,
                                      createFlightOnAssignment:
                                          organisationDefaults.createFlightOnAssignment
                                  },
                            data => {
                                const command: UpdateAutomaticMissionConfigurationCommand =
                                    {
                                        type: data.type,
                                        maxDistance: data.maxDistance ?? 0,
                                        maxTimeDifference:
                                            data.maxTimeDifference ?? 0,
                                        missionWorkflowId:
                                            data.missionWorkflowId,
                                        operationTypeId: data.operationTypeId,
                                        workgroupId: data.workgroupId,
                                        projectId: data.projectId,
                                        missionObjectiveId:
                                            data.missionObjectiveId,
                                        useCollectionSourceOwner:
                                            data.useCollectionSourceOwner ??
                                            false,
                                        remotePilotId:
                                            data.useCollectionSourceOwner ===
                                            false
                                                ? data.remotePilotId
                                                : null,
                                        requireRpa: data.requireRpa ?? true,
                                        createFlightOnAssignment:
                                            data.createFlightOnAssignment ??
                                            false,
                                        missionPerRpa:
                                            data.missionPerRpa ?? false
                                    };

                                pilotService.ngOnDestroy();

                                return firstValueFrom(
                                    this.flightLogCollectionService.updateAutomaticMissionConfiguration(
                                        entry.id,
                                        command
                                    )
                                );
                            },
                            'modal-task'
                        )
                        .then(DO_NOTHING)
                        .catch(() => {
                            pilotService.ngOnDestroy();
                        });
                }
            );
    }

    showOrganisationAutomaticMissionConfiguration() {
        combineLatest([
            this.enhancedHelpService.findByScreen(
                'flight-log-mission-creation-settings'
            ),
            this.flightLogCollectionService.findOrganisationAutomaticMissionConfiguration(
                this.organisationId
            ),
            this.missionService.findMissionTypes(this.organisationId),
            this.missionWorkflowService.findWorkflows(this.organisationId),
            this.workgroupsSource.findWorkgroups(this.organisationId),
            this.projectService.find(this.organisationId, false),
            this.missionObjectiveService.find(this.organisationId, false)
        ])
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([
                    help,
                    config,
                    missionTypeList,
                    workflowList,
                    workgroupList,
                    projectList,
                    missionObjectiveList
                ]) => {
                    const helpText =
                        help == null
                            ? {}
                            : help.reduce(
                                  (
                                      acc: { [component: string]: string },
                                      h: EnhancedHelpDto
                                  ) => ({ ...acc, [h.component]: h.helpText }),
                                  {}
                              );
                    this.commonDialoguesService
                        .showFormlyDialogue<
                            Omit<
                                UpdateOrganisationAutomaticMissionConfigurationCommand,
                                'organisationId'
                            >,
                            OrganisationAutomaticMissionConfigurationDto
                        >(
                            'Default Automatic Mission Creation Settings',
                            'Save',
                            true,
                            true,
                            [
                                {
                                    key: `section-required-info`,
                                    type: 'info',
                                    props: {
                                        message: helpText['organisationInfo']
                                    }
                                },
                                {
                                    key: 'type',
                                    type: 'ng-select',
                                    props: {
                                        label: 'Type (action)',
                                        required: true,
                                        description: helpText['type'],
                                        labelProp: 'name',
                                        valueProp: 'value',
                                        options: [
                                            {
                                                name: 'Suggest Mission Assignment',
                                                value: 'MISSION_SUGGESTION'
                                            },
                                            {
                                                name: 'Automatic Assignment',
                                                value: 'MISSION_ASSIGNMENT'
                                            },
                                            {
                                                name: 'Automatic Mission Creation',
                                                value: 'MISSION_CREATION'
                                            },
                                            {
                                                name: 'Disabled',
                                                value: 'DISABLED'
                                            }
                                        ]
                                    }
                                },
                                {
                                    key: `section-required`,
                                    type: 'section-heading',
                                    hideExpression: 'model.type === "DISABLED"',
                                    props: {
                                        label: 'Required parameters'
                                    }
                                },
                                {
                                    key: `section-required-info`,
                                    type: 'info',
                                    hideExpression: 'model.type === "DISABLED"',
                                    props: {
                                        message:
                                            helpText['section-required-info']
                                    }
                                },
                                {
                                    key: 'maxDistance',
                                    type: 'input',
                                    hideExpression: 'model.type === "DISABLED"',
                                    props: {
                                        label: 'Maximum distance (m)',
                                        type: 'number',
                                        required: true,
                                        description: helpText['maxDistance']
                                    }
                                },
                                {
                                    key: 'maxTimeDifference',
                                    type: 'duration',
                                    hideExpression: 'model.type === "DISABLED"',
                                    props: {
                                        label: 'Maximum time difference',
                                        required: true,
                                        description:
                                            helpText['maxTimeDifference']
                                    }
                                },
                                {
                                    key: 'requireRpa',
                                    type: 'checkbox',
                                    hideExpression: 'model.type === "DISABLED"',
                                    props: {
                                        label: 'Log requires an RPA',
                                        required: true,
                                        description: helpText['requireRpa']
                                    }
                                },
                                {
                                    key: `section-optional`,
                                    type: 'section-heading',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Optional parameters'
                                    }
                                },
                                {
                                    key: `section-optional-info`,
                                    type: 'info',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        message:
                                            helpText['section-optional-info']
                                    }
                                },
                                {
                                    key: 'useCollectionSourceOwner',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Collection Source Owner is RPIC',
                                        required: true,
                                        description:
                                            helpText['useCollectionSourceOwner']
                                    }
                                },
                                {
                                    key: 'operationTypeId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Mission Type',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: missionTypeList,
                                        clearable: true,
                                        required: false,
                                        description: helpText['operationTypeId']
                                    }
                                },
                                {
                                    key: 'missionWorkflowId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Workflow',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: workflowList,
                                        clearable: true,
                                        required: false,
                                        description:
                                            helpText['missionWorkflowId']
                                    }
                                },

                                {
                                    key: 'workgroupId',
                                    type: 'ng-select',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Workgroup',
                                        labelProp: 'name',
                                        valueProp: 'id',
                                        options: workgroupList,
                                        clearable: true,
                                        required: false,
                                        description: helpText['workgroupId']
                                    }
                                },
                                ...(projectList.length > 0
                                    ? [
                                          {
                                              key: 'projectId',
                                              type: 'ng-select',
                                              hideExpression:
                                                  'model.type !== "MISSION_CREATION"',
                                              props: {
                                                  label: 'Project',
                                                  labelProp: 'name',
                                                  valueProp: 'id',
                                                  options: projectList,
                                                  clearable: true,
                                                  required: false,
                                                  description:
                                                      helpText['projectId']
                                              }
                                          }
                                      ]
                                    : []),
                                ...(missionObjectiveList.length > 0
                                    ? [
                                          {
                                              key: 'missionObjectiveId',
                                              type: 'ng-select',
                                              hideExpression:
                                                  'model.type !== "MISSION_CREATION"',
                                              props: {
                                                  label: 'Mission Objective',
                                                  labelProp: 'name',
                                                  valueProp: 'id',
                                                  options: missionObjectiveList,
                                                  clearable: true,
                                                  required: false,
                                                  description:
                                                      helpText[
                                                          'missionObjectiveId'
                                                      ]
                                              }
                                          }
                                      ]
                                    : []),
                                {
                                    key: 'createFlightOnAssignment',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_ASSIGNMENT"',
                                    props: {
                                        label: 'Create Flight on Assigned Mission',
                                        required: true,
                                        description:
                                            helpText['createFlightOnAssignment']
                                    }
                                },
                                {
                                    key: 'missionPerRpa',
                                    type: 'checkbox',
                                    hideExpression:
                                        'model.type !== "MISSION_CREATION"',
                                    props: {
                                        label: 'Separate Mission per RPA',
                                        required: true,
                                        description: helpText['missionPerRpa']
                                    }
                                }
                            ],
                            config != null
                                ? {
                                      type: config.type,
                                      maxDistance: config.maxDistance,
                                      maxTimeDifference:
                                          config.maxTimeDifference,
                                      useCollectionSourceOwner:
                                          config.useCollectionSourceOwner,
                                      operationTypeId: config.operationType?.id,
                                      missionWorkflowId:
                                          config.missionWorkflow?.id,
                                      projectId: config.project?.id,
                                      missionObjectiveId:
                                          config.missionObjective?.id,
                                      requireRpa: config.requireRpa,
                                      createFlightOnAssignment:
                                          config.createFlightOnAssignment,
                                      missionPerRpa: config.missionPerRpa
                                  }
                                : {
                                      type: 'ORGANISATION_DEFAULT',
                                      requireRpa: true
                                  },
                            data => {
                                const command: UpdateOrganisationAutomaticMissionConfigurationCommand =
                                    {
                                        organisationId: this.organisationId,
                                        type: data.type,
                                        maxDistance: data.maxDistance ?? 0,
                                        maxTimeDifference:
                                            data.maxTimeDifference ?? 0,
                                        missionWorkflowId:
                                            data.missionWorkflowId,
                                        operationTypeId: data.operationTypeId,
                                        projectId: data.projectId,
                                        missionObjectiveId:
                                            data.missionObjectiveId,
                                        useCollectionSourceOwner:
                                            data.useCollectionSourceOwner ??
                                            true,
                                        requireRpa: data.requireRpa ?? true,
                                        createFlightOnAssignment:
                                            data.createFlightOnAssignment ??
                                            false,
                                        missionPerRpa:
                                            data.missionPerRpa ?? false
                                    };
                                return firstValueFrom(
                                    this.flightLogCollectionService.updateOrganisationAutomaticMissionConfiguration(
                                        command
                                    )
                                );
                            },
                            'modal-task'
                        )
                        .then(DO_NOTHING)
                        .catch(DO_NOTHING);
                }
            );
    }
}
